import React, {useMemo} from "react";
import {FormRenderer} from "./FormRenderer.tsx";
import {FormPermissions, ReportForm} from "../../api/ApiTypes.ts";
import {FormSchema} from "./DynamicFormTypes.ts";
import {getFormPermissions, streetProperCase, SUBMIT_MODE, toTitleCase} from "./dyanimcFormUtil.tsx";
import {Star} from "lucide-react";
import {HeaderInfo} from "./HeaderInfoBar.tsx";
import {useAuth} from "../../context/AuthContext.tsx";

export interface ReportFormLayoutProps {
    reportForm: ReportForm;
    mode: SUBMIT_MODE;
}

const ReportFormLayoutRenderer: React.FC<ReportFormLayoutProps> = ({
                                                                      reportForm,
                                                                      mode = SUBMIT_MODE.READ_ONLY
                                                                  }) => {

    const auth = useAuth();

    console.log("Report Form Layout Rendered in ", mode, " with reportForm: ", reportForm)
    // Parse the schema once using useMemo to prevent repeated parsing on every render
    const formSchema = useMemo(() => {
        try {
            return JSON.parse(reportForm.schema) as FormSchema;
        }
        catch (error) {
            console.error("Error parsing form schema:", error);
            return {} as FormSchema;
        }
    }, [reportForm.schema]);

    // Compute permissions here so both the layout and FormRenderer share the same resolved permissions
    const formPermissions = useMemo<FormPermissions>(() => {
        const permissions = getFormPermissions(mode, reportForm.submission, auth.me?.userType);
        console.info("ReportFormLayout: resolved form permissions", permissions);
        return permissions;
    }, [mode, reportForm.submission?.id, auth.me?.userType]);

    const headerInfo: HeaderInfo | undefined = useMemo(() => {
        try {
            if (!reportForm.metaData) return undefined;
            const addressParts = [
                streetProperCase(reportForm.metaData.streetAddress),
                streetProperCase(reportForm.metaData.streetAddressLine2)
            ].filter(Boolean);

            const cityStateZip = [
                toTitleCase(reportForm.metaData.city),
                reportForm.metaData.state?.toUpperCase(),
                reportForm.metaData.zip
            ].filter(Boolean).join(', ');

            return {
                street: addressParts.join(', '),
                cityStateZip,
                county: toTitleCase(reportForm.metaData.county),
                id: reportForm.metaData?.orderDetails?.appraisal?.usaFileNumber,
                description: reportForm.definition?.description,
                submissionStatus: reportForm?.submission?.status
            };
        }
        catch (error) {
            console.error("Error parsing form schema:", error);
            return undefined;
        }
    }, [reportForm.metaData, reportForm.definition]);

    // Use the resolved submitMode from permissions (not the requested mode)
    // e.g. APPRAISER_SUBMIT may resolve to READ_ONLY if already submitted
    const resolvedMode = formPermissions.submitMode;

    return (
        <div className="space-y-6 w-full min-w-0 pt-20">
            {/* Mode banners based on resolved permissions */}
            {SUBMIT_MODE.QC === resolvedMode && (
                <div className="text-sm text-slate-500 italic">
                    This form is QC purposes only. Note that certain features may be disabled.
                </div>
            )}
            {SUBMIT_MODE.READ_ONLY === resolvedMode && (
                <div className="text-sm text-slate-500 italic">
                    This form is in READ ONLY mode
                </div>
            )}
            {SUBMIT_MODE.ADMIN === resolvedMode && (
                <div className="text-sm text-slate-500 italic">
                    This form is in Admin Mode
                </div>
            )}
            {headerInfo && (
                <div className="bg-indigo-600 text-white p-4 sm:p-6 rounded-md shadow-lg">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 min-w-0">
                            <svg className="w-5 h-5 text-indigo-100 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                            </svg>
                            <div className="flex items-center gap-1 text-base sm:text-lg font-medium min-w-0">
                                <span className="truncate">{headerInfo.street}</span>
                                <span className="text-indigo-200 flex-shrink-0">•</span>
                                <span className="truncate">{headerInfo.cityStateZip}</span>
                                {headerInfo.county && (
                                    <>
                                        <span className="text-indigo-200 flex-shrink-0">•</span>
                                        <span className="text-indigo-100 truncate">{headerInfo.county} County</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Order Info */}
                    <div className="mt-1 pt-1 border-t border-indigo-500/30">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-sm text-indigo-100">
                            {headerInfo.id && (
                                <div className="min-w-0">
                                    <span className="font-medium">Order ID:</span> <span className="break-all">{headerInfo.id}</span>
                                </div>
                            )}
                            {headerInfo.description && (
                                <div className="min-w-0">
                                    <span className="font-medium">Form:</span> <span className="truncate">{headerInfo.description}</span>
                                </div>
                            )}
                            {headerInfo?.submissionStatus && (
                                <div className="min-w-0">
                                    <span className="font-medium">Status:</span> <span className="truncate">{headerInfo?.submissionStatus}</span>
                                </div>
                            )}
                            {SUBMIT_MODE.APPRAISER_SUBMIT !== resolvedMode && (
                                <div className="flex items-center gap-1">
                                    <span className="font-medium">{resolvedMode}</span> <Star fill={"yellow"}/>
                                </div>
                            )}

                        </div>
                    </div>
                </div>
            )}



            {/* Main Form */}
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-slate-200 w-full min-w-0">
                <FormRenderer
                    schema={formSchema}
                    metaData={reportForm.metaData}
                    formSubmission={reportForm.submission}
                    headerInfo={headerInfo}
                    mode={mode}
                    formPermissions={formPermissions}
                />
            </div>
        </div>
    );
};

export const ReportFormLayout = React.memo(ReportFormLayoutRenderer);
